import React, { useState, useEffect, useContext } from "react"
import moment from "moment"

import { AlertsContext } from "../../../utils/providers/alerts"
import { db, funEU } from "../../../utils/firebase"

import Window from "../../structure/window/window"
import WindowCard from "../../ui/window-card/window-card"
import Button from "../../ui/button/button"

import styles from "./apply-credit-to-booking.module.scss"

const applyCallable = funEU.httpsCallable("applyClientCreditToBooking")

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

/**
 * Modal for moving a paid standalone client payment onto a specific
 * booking. Lists the client's non-removed bookings (excludes cancelled).
 * Server-side enforces that the chosen booking belongs to the client and
 * hasn't been removed.
 */
export default function ApplyCreditToBooking({ clientID, paymentID, linkID, amount, reference, close }) {
	const { pushAlert } = useContext(AlertsContext)

	const [bookings, setBookings] = useState([])
	const [loading, setLoading] = useState(true)
	const [selectedBookingId, setSelectedBookingId] = useState(null)
	const [submitting, setSubmitting] = useState(false)

	useEffect(() => {
		if (!clientID) return

		let cancelled = false

		db.collection("bookings")
			.where("client", "==", clientID)
			.get()
			.then((snap) => {
				if (cancelled) return
				const list = snap.docs
					.map((d) => ({ id: d.id, ...d.data() }))
					.filter((b) => !b.removed)
					.sort((a, b) => (b.date_from?.seconds || 0) - (a.date_from?.seconds || 0))
				setBookings(list)
			})
			.catch((err) => {
				if (cancelled) return
				pushAlert({ type: "ERROR", title: err?.message || "Could not load bookings." })
			})
			.finally(() => {
				if (cancelled) return
				setLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [clientID, pushAlert])

	const submit = async () => {
		if (!selectedBookingId) return

		setSubmitting(true)
		try {
			await applyCallable({ clientID, paymentID, bookingID: selectedBookingId, linkID })
			pushAlert({ type: "SUCCESS", title: "Credit applied to booking" })
			close()
		} catch (err) {
			pushAlert({ type: "ERROR", title: err?.message || "Could not apply credit." })
			setSubmitting(false)
		}
	}

	return (
		<Window
			title="Apply credit to a booking"
			className="slim"
			close={() => close()}>
			<WindowCard>
				<div className={styles.summary}>
					<div>
						<p className={styles.summaryLabel}>Credit amount</p>
						<p className={styles.summaryValue}>£{formatGbp(amount)}</p>
					</div>
					<div>
						<p className={styles.summaryLabel}>Reference</p>
						<p className={styles.summaryRef}>{reference}</p>
					</div>
				</div>

				<p className={styles.heading}>Which booking?</p>

				{loading && <p className={styles.empty}>Loading bookings...</p>}

				{!loading && bookings.length === 0 && (
					<p className={styles.empty}>This client has no active bookings to apply the credit to.</p>
				)}

				{!loading && bookings.length > 0 && (
					<div className={styles.bookingList}>
						{bookings.map((b) => {
							const fromDate = b.date_from?.seconds ? moment(b.date_from.seconds, "X").format("DD MMM YYYY") : "—"
							const total = Number(b.margins?.customer_price ?? b.total_cost ?? 0)
							const paid = Number(b.paid_by_client ?? 0)
							const outstanding = Math.max(total - paid, 0)
							const isSelected = selectedBookingId === b.id

							return (
								<button
									key={b.id}
									type="button"
									className={[styles.bookingOption, isSelected ? styles.bookingSelected : ""].join(" ")}
									onClick={() => setSelectedBookingId(b.id)}>
									<div className={styles.bookingTop}>
										<span className={styles.bookingRef}>{b.reference || `Booking ${b.id.slice(-6)}`}</span>
										<span className={styles.bookingDate}>{fromDate}</span>
									</div>
									<div className={styles.bookingMeta}>
										<span>Total £{formatGbp(total)}</span>
										<span>Paid £{formatGbp(paid)}</span>
										<span>Outstanding £{formatGbp(outstanding)}</span>
									</div>
								</button>
							)
						})}
					</div>
				)}

				{bookings.length > 0 && (
					<div className={styles.submitWrap}>
						<Button
							label="Apply credit"
							fullWidth={true}
							loading={submitting}
							loadingText="Applying..."
							onClick={submit}
							disabled={!selectedBookingId}
						/>
					</div>
				)}
			</WindowCard>
		</Window>
	)
}
